import {cn, Typography} from '@sollapay/ui';
import {Checkbox} from '@sollapay/ui/components';
import {useId} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import type {FC} from 'react';

/** Who grants the power of attorney, and on which account. */
export interface PowerOfAttorneyGrantor {
  trusteeName: string;
  accountName: string;
}

export interface SharePowerOfAttorneyConsentProps {
  grantor: PowerOfAttorneyGrantor;
  operatorNames: string[];
  isGranted: boolean;
  onGrantedChange: (isGranted: boolean) => void;
}

/** Operators act in the trustee's name, so making someone an operator needs this consent. */
export const SharePowerOfAttorneyConsent: FC<SharePowerOfAttorneyConsentProps> = ({
  grantor,
  operatorNames,
  isGranted,
  onGrantedChange
}) => {
  const {t} = useTranslation('trustAccounts');
  const checkboxId = useId();
  const titleId = useId();
  const consentId = useId();

  return (
    <label
      htmlFor={checkboxId}
      data-slot="share-power-of-attorney"
      className={cn(
        'flex cursor-pointer items-start gap-2.5 rounded-md border bg-background-surface px-3 py-2.5 transition-colors',
        isGranted ? 'border-brand-accent' : 'border-border'
      )}
    >
      <Checkbox
        id={checkboxId}
        checked={isGranted}
        onCheckedChange={checked => onGrantedChange(checked === true)}
        aria-labelledby={titleId}
        aria-describedby={consentId}
        className="mt-0.5 shrink-0"
      />
      <span className="flex flex-col gap-1">
        <Typography as="span" id={titleId} size="sm" weight="semibold">
          {t('share.powerOfAttorney.title')}
        </Typography>
        <ConsentText id={consentId} grantor={grantor} operatorNames={operatorNames} />
      </span>
    </label>
  );
};

type ConsentTextProps = Pick<SharePowerOfAttorneyConsentProps, 'grantor' | 'operatorNames'> & {
  id: string;
};

const ConsentText: FC<ConsentTextProps> = ({id, grantor, operatorNames}) => {
  const {i18n} = useTranslation('trustAccounts');
  const operators = new Intl.ListFormat(i18n.language, {type: 'conjunction'}).format(operatorNames);

  return (
    <Typography
      as="span"
      id={id}
      size="xs"
      color="secondary"
      className="leading-relaxed whitespace-normal"
    >
      <Trans
        ns="trustAccounts"
        i18nKey="share.powerOfAttorney.consent"
        count={operatorNames.length}
        values={{trustee: grantor.trusteeName, operators, account: grantor.accountName}}
        components={{b: <strong />}}
      />
    </Typography>
  );
};
